import Toastify from "toastify-js";

/**
 * Manages a screensaver video based on user inactivity.
 * It tracks mouse movement to reset an inactivity timer and shows/hides
 * the screensaver video accordingly. It also provides a toggle button
 * to enable/disable the screensaver functionality.
 */
class Screensaver {
  /**
   * @param {string} videoElementSelector - CSS selector for the screensaver video element.
   * @param {string} videoTriggerElementSelector - CSS selector for the button/element that toggles the screensaver.
   * @param {number} inactivityDuration - Duration in milliseconds after which the screensaver activates due to inactivity.
   */
  constructor(
    videoElementSelector,
    videoTriggerElementSelector,
    inactivityDuration
  ) {
    // Cache DOM elements for efficient access throughout the class's lifecycle.
    this.videoElement = document.querySelector(videoElementSelector);
    this.videoTrigger = document.querySelector(videoTriggerElementSelector);
    this.inactivityDuration = inactivityDuration;
    this.timer = null; // Stores the setTimeout ID for inactivity timer.
    this.isEnabled = true; // Flag to control if the screensaver functionality is active.

    // Bind event handler methods to the class instance.
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleChange = this.handleChange.bind(this);

    // Attach the click listener to the toggle button once during construction.
    this.videoTrigger.addEventListener("change", this.handleChange);

    // Initialize the screensaver functionality.
    this.init();

    if (window.debug) {
      // console.log(`Screensaver instance created.`);
    }
  }

  /**
   * Initializes or re-initializes the screensaver.
   * Starts the inactivity timer and attaches the mousemove listener to the document.
   */
  init() {
    this.startInactivityTimer();
    // Attach the mousemove listener to the document.
    // This listener is crucial for detecting user activity across the entire page.
    // It's added here and removed in `destroy()` to prevent duplicate listeners.
    document.addEventListener("mousemove", this.handleMouseMove);

    if (window.debug) {
      // console.log(`Screensaver initialized (listeners and timer active).`);
    }
  }

  /**
   * Handles clicks on the screensaver toggle button.
   * Toggles the screensaver's enabled state, updates UI, and shows a toast notification.
   * @param {Event} e - The click event object.
   */
  handleChange(e) {
    // Toggle the enabled state
    this.isEnabled = e.target.checked;

    if (this.isEnabled) {
      this.init(); // Re-initialize (start timer, add mousemove listener)
    } else {
      this.destroy(); // Destroy (clear timer, remove mousemove listener)
    }

    // Display a toast notification to inform the user about the status change.
    // Toastify({
    //   text: this.isEnabled ? "Screensaver Enabled" : "Screensaver Disabled",
    //   duration: 2000,
    //   gravity: "bottom",
    //   position: "left",
    //   className: "!rounded-lg !shadow !font-bold !text-lg",
    //   style: { background: "#000000" },
    // }).showToast();

    if (window.debug) {
      console.log(
        `Screensaver status toggled to: ${
          this.isEnabled ? "Enabled" : "Disabled"
        }`
      );
    }
  }

  /**
   * Handles mouse movement events.
   * Resets the inactivity timer and hides the screensaver video if it's active.
   * This method is called frequently but performs highly optimized operations.
   * @param {Event} e - The mousemove event object.
   */
  handleMouseMove(e) {
    if (!this.isEnabled) return; // Do nothing if screensaver is disabled

    this.resetInactivityTimer();
    this.hideVideo();
  }

  /**
   * Starts the inactivity timer.
   * If the timer completes, the screensaver video is shown and played.
   */
  startInactivityTimer() {
    this.timer = setTimeout(() => {
      if (this.isEnabled) {
        // Only show if screensaver is still enabled
        this.showAndPlayVideo();
      }
    }, this.inactivityDuration);
    if (window.debug) {
      // console.log(
      //   `Inactivity timer started. Will activate in ${
      //     this.inactivityDuration / 1000
      //   } seconds.`
      // );
    }
  }

  /**
   * Resets the inactivity timer.
   * Clears any existing timer and starts a new one.
   */
  resetInactivityTimer() {
    clearTimeout(this.timer); // Cancel any pending timer.
    this.startInactivityTimer(); // Start a new timer.
    if (window.debug) {
      // console.log(`Inactivity timer reset.`);
    }
  }

  /**
   * Shows the screensaver video element and starts playing it.
   * Direct DOM style manipulation and video API calls are efficient.
   */
  showAndPlayVideo() {
    if (this.videoElement) {
      this.videoElement.style.display = "block"; // Makes the element visible.
      this.videoElement.play(); // Starts video playback.
      if (window.debug) {
        // console.log(`Screensaver video shown and playing.`);
      }
    }
  }

  /**
   * Hides the screensaver video element, pauses it, and resets its playback position.
   * Direct DOM style manipulation and video API calls are efficient.
   */
  hideVideo() {
    if (this.videoElement && this.videoElement.style.display !== "none") {
      // Only hide if currently visible
      this.videoElement.style.display = "none"; // Hides the element.
      this.videoElement.pause(); // Pauses video playback.
      this.videoElement.currentTime = 0; // Resets video to the beginning.
      if (window.debug) {
        // console.log(`Screensaver video hidden, paused, and reset.`);
      }
    }
  }

  /**
   * Destroys the screensaver's active components.
   * Clears the inactivity timer and removes the global mousemove listener.
   * This is crucial for preventing memory leaks when the screensaver is disabled.
   */
  destroy() {
    if (window.debug) {
      // console.log(
      //   `Screensaver components being destroyed (timer cleared, mousemove listener removed).`
      // );
    }
    clearTimeout(this.timer); // Ensure no pending timer fires.
    this.timer = null; // Nullify the timer ID.

    // Remove the global mousemove listener. This is critical.
    // Because `this.handleMouseMove` is a stable, bound reference, `removeEventListener` works correctly.
    // This prevents duplicate listeners from accumulating if `init()` is called again.
    document.removeEventListener("mousemove", this.handleMouseMove);

    // Ensure the video is hidden and reset when screensaver is disabled.
    this.videoElement.style.display = "none";
    this.videoElement.pause();
    this.videoElement.currentTime = 0;
  }
}

export { Screensaver };
